/** @jsxImportSource @emotion/react */
import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import 'features/geocoder/utils/registerProjections';
import MapArea from 'features/userData/map/MapArea';
import GeocoderResultsTable from './GeocoderResultsTable';
import { style as userDataPanelStyles } from 'features/userData/style';
import useGeocoderAppState from 'features/geocoder/hooks/useGeocoderAppState';
import GeocoderControlPanel from './layer/GeocoderControlPanel';
import WorkspaceLayout from 'components/my_map/workspace/WorkspaceLayout';
import { MY_MAP_WORKSPACE_TABS } from 'components/my_map/workspace/tabs';
import useUploadWorkflow from '../hooks/useUploadWorkflow';
import DataPreviewGrid from './DataPreviewGrid';
import { Box, Typography } from '@mui/material';

import {
    encodingOptions,
    delimiterOptions,
    geocodingTypeOptions,
    coordSystemOptions,
} from 'constants/geocoder/options';
import { ConstructionOutlined } from '@mui/icons-material';

const GeocoderContent = () => {
    const {
        panelMode,
        setPanelMode,
        activeTab: geocoderPanelTab,
        setActiveTab: setGeocoderPanelTab,
        layers,
        selectedLayerIndex,
        setSelectedLayerIndex,
        isAttributeTableExpanded,
        // setIsAttributeTableExpanded,
        handleTableExpand,
        focusPoint,
        historyRecords,
        manualHistoryRecords,
        fileHistoryMeta,
        manualAttributeRecords,
        fileAttributeRecords,
        historyGeojson,
        downloadOptions,
        handleHistoryRowSelect,
        pendingFitExtent,
        consumeFitExtent,
        handleResultRecord,
        addLayerFromGeoJSON,
        clearFileLayers,
        updateRecordInLayer,
    } = useGeocoderAppState();

    const {
        previewGeoJSON,
        gridRows: previewRows,
        gridColumns: previewColumns,
        gridInfo,
        dataEpsg,
        file,
        isFileDeletedAfterUpload,
        lastStatus
    } = useUploadWorkflow({
        encodingOptions,
        delimiterOptions,
        geocodingTypeOptions,
        coordSystemOptions,
    });

    const navigate = useNavigate();

    const downloadOptionsWithIcon = useMemo(
        () =>
            downloadOptions.map((opt) => ({
                ...opt,
                icon: <DownloadForOfflineRoundedIcon fontSize="small" />,
            })),
        [downloadOptions]
    );

    const controlPanel = (
        <div css={userDataPanelStyles.tabContentArea}>
            <GeocoderControlPanel
                panelMode={panelMode}
                setPanelMode={setPanelMode}
                geocodingTypeOptions={geocodingTypeOptions}
                coordSystemOptions={coordSystemOptions}
                encodingOptions={encodingOptions}
                delimiterOptions={delimiterOptions}
                downloadOptions={downloadOptionsWithIcon}
                handleResultRecord={handleResultRecord}
                addLayerFromGeoJSON={addLayerFromGeoJSON}
                manualHistoryRecords={manualHistoryRecords}
                fileHistoryMeta={fileHistoryMeta}
                onHistoryRecordSelect={handleHistoryRowSelect}
                isFileDeletedAfterUpload={isFileDeletedAfterUpload}
                onClearFileLayers={clearFileLayers}
            />
        </div>
    );

    // 파일 업로드 시 previewRows에 데이터가 들어오면 속성 테이블 자동 확장
    useEffect(() => {
        if (panelMode === '1') return;

        if (file && previewRows?.length > 0 && !isAttributeTableExpanded) {
            // setIsAttributeTableExpanded(true);
            handleTableExpand(true);
        } else if (!file && isAttributeTableExpanded && isFileDeletedAfterUpload) {
            // setIsAttributeTableExpanded(false);
            handleTableExpand(false);
            console.log("닫기")
        }
    // }, [file, previewRows?.length, isAttributeTableExpanded, setIsAttributeTableExpanded]);
    // }, [file, previewRows?.length, isAttributeTableExpanded, handleTableExpand]);
    }, [file, previewRows?.length, handleTableExpand]);

    const handleWorkspaceTabChange = (tabId) => {
        if (tabId === 'user-data') {
            navigate('/my_map/user_data');
        }
    };

    const currentGeojson = useMemo(() => {
        if (!historyGeojson) return { type: 'FeatureCollection', features: [] };

        const deepCopy = JSON.parse(JSON.stringify(historyGeojson));

        const regex = /총\s*:\s*([\d,]+)건,\s*성공\s*:\s*([\d,]+)건,\s*실패\s*:\s*([\d,]+)건\s*완료되었습니다/;
        const match = lastStatus.replace(/\./g, '').match(regex);

        let successCount = 0;

        if (match && match.length > 2) {
            successCount = parseInt(match[2].replace(/,/g, ''), 10);
        }

        if (isFileDeletedAfterUpload || successCount === 0) {
            // 파일 삭제 시, 파일 업로드 기록 (source: 'file')만 currentGeojson에서 제거
            deepCopy.features = deepCopy.features.filter(feature => 
                feature.properties.source !== 'file'
            );
        }

        console.log("attrGeojson = ", deepCopy)

        return deepCopy;
    }, [historyGeojson, isFileDeletedAfterUpload, lastStatus]);
    
    // const hasNoGeojsonResults = historyGeojson?.features?.length === 0 || previewRows.length === 0 || isFileDeletedAfterUpload;
    const hasNoGeojsonResults = currentGeojson?.features?.length === 0 || previewRows.length === 0;

    const attributeRecords = panelMode === '2' ? fileAttributeRecords : manualAttributeRecords;

    const attributeTitle = useMemo(() => {
        if (panelMode === '2') {
            if (previewRows?.length > 0 && hasNoGeojsonResults) {
                return '데이터 미리보기 [파일 업로드]';
            }
            return '지오코딩 결과 [파일 업로드]';
        }
        return '지오코딩 결과 [직접 입력]';
    }, [panelMode, previewRows, hasNoGeojsonResults]);

    const sidebarContent =
        geocoderPanelTab === 'layer'
            ? controlPanel
            : <div css={userDataPanelStyles.noDataMessage}>기능 구현 예정입니다.</div>;

    // attributeContent 조건부 렌더링 
    const renderAttributeContent = useMemo(() => {
        if (panelMode === '2' && previewRows?.length > 0 && hasNoGeojsonResults) {
            return (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {`${gridInfo.rows}행 / ${gridInfo.columns}열 (${dataEpsg || '알수없음'})`}
                    </Typography>
                    <DataPreviewGrid 
                        rows={previewRows} 
                        columns={previewColumns} 
                        height={260} 
                        perPage={5} 
                    />
                </Box>
            );
        }

        if (panelMode === '2' && hasNoGeojsonResults) {
            return (
                <div style={{ padding: "16px", textAlign: "center", color: "#6b7280" }}>
                    속성 데이터가 없습니다.
                </div>
            );
        }

        return (
            <GeocoderResultsTable
                records={attributeRecords}
                onSelectRecord={handleHistoryRowSelect}
                onRecordUpdate={updateRecordInLayer}
            />
        );
    }, [panelMode, previewRows, hasNoGeojsonResults, gridInfo, dataEpsg, previewColumns, attributeRecords, handleHistoryRowSelect]);

    return (
        <WorkspaceLayout
            tabs={MY_MAP_WORKSPACE_TABS}
            activeTab="geocoder"
            onTabChange={handleWorkspaceTabChange}
            sidebarContent={sidebarContent}
        >
            <MapArea
                layers={layers}
                selectedLayerIndex={selectedLayerIndex}
                // attrGeojson={historyGeojson}
                attrGeojson={currentGeojson}
                isTableExpanded={isAttributeTableExpanded}
                // setIsTableExpanded={setIsAttributeTableExpanded}
                handleTableExpand={handleTableExpand}
                focusPoint={focusPoint}
                attributeTitle={attributeTitle}
                attributeContent={renderAttributeContent}
                disableAutoFit
                fitExtent={pendingFitExtent}
                onFitExtentConsumed={consumeFitExtent}
                isFileDeletedAfterUpload={isFileDeletedAfterUpload}
            />
        </WorkspaceLayout>
    );
};

export default GeocoderContent;